import React from "react";
import {
  ArrowRight, BadgeCheck, FileSignature, Link2, MessageSquare, Mic, MonitorUp, PhoneOff, Pill, Send, ShieldCheck, Stethoscope, UserPlus, Users, Video,
} from "lucide-react";
import Reveal from "./Reveal";

/* ------------------------------------------------------------------ people in the call (Pexels photos, see public/images/telehealth/CREDITS.md) */

const Photo = ({ src, alt, position, mirror }) => (
  <img src={src} alt={alt} loading="lazy" decoding="async" className={`h-full w-full object-cover ${mirror ? "-scale-x-100" : ""}`} style={{ objectPosition: position }} />
);
export const DoctorPortrait = () => <Photo src="/images/telehealth/doctor.jpg" alt="Doctor smiling during a video consultation" position="72% 26%" mirror />;
const PatientPortrait = () => <Photo src="/images/telehealth/patient.jpg" alt="Patient joining the consultation from home" position="38% 22%" />;
const GuestPortrait = () => <Photo src="/images/telehealth/guest.jpg" alt="Family member who joined with an invite link" position="52% 45%" />;

/* ------------------------------------------------------------------ the call */

const Ctrl = ({ icon: Icon, label, danger, badge }) => (
  <span className="flex flex-col items-center gap-1">
    <span className={`relative flex h-9 w-9 items-center justify-center rounded-full sm:h-10 sm:w-10 ${danger ? "bg-rose-600 text-white" : "bg-white/15 text-white"}`}>
      <Icon className="h-4 w-4" />
      {badge && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-ink-900">{badge}</span>}
    </span>
    <span className="hidden text-[10px] text-slate-300 sm:block">{label}</span>
  </span>
);

export const VideoCallScene = ({ bare = false }) => (
  <div className="relative">
    <div className="overflow-hidden rounded-2xl bg-ink-900 shadow-[0_40px_90px_-30px_rgba(11,35,73,.6)] ring-1 ring-white/10">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">Consultation with Dr. Meera Iyer</p>
          <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-500" /> 08:42 · 3 in call · secure connection
          </p>
        </div>
        <span className="hidden items-center gap-1 rounded-full bg-care-500/15 px-2.5 py-1 text-[10px] font-semibold text-care-300 sm:inline-flex">
          <ShieldCheck className="h-3 w-3" /> HD
        </span>
      </div>
      <div className="relative mx-3 aspect-[16/10] overflow-hidden rounded-xl">
        <DoctorPortrait />
        <span className="absolute bottom-3 left-3 rounded-md bg-black/55 px-2 py-1 text-[11px] font-medium text-white">Dr. Meera Iyer · General Medicine</span>
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-black/40 px-1.5 py-1">
          {[3, 6, 9, 5].map((h, i) => <span key={i} className="w-1 rounded-full bg-care-400" style={{ height: h * 1.6 }} />)}
        </span>
        <div className="absolute right-3 top-3 w-[30%] overflow-hidden rounded-lg shadow-xl ring-2 ring-white/80">
          <div className="aspect-[16/11]"><PatientPortrait /></div>
          <span className="absolute bottom-1 left-1 rounded bg-black/55 px-1.5 py-0.5 text-[9px] text-white">You · Ananya</span>
        </div>
        <div className="absolute bottom-3 right-3 w-[22%] overflow-hidden rounded-lg shadow-xl ring-2 ring-care-400">
          <div className="aspect-[16/11]"><GuestPortrait /></div>
          <span className="absolute bottom-1 left-1 rounded bg-black/55 px-1.5 py-0.5 text-[9px] text-white">Grandmother · guest</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 px-3 py-3 sm:gap-4">
        <Ctrl icon={Mic} label="Mute" />
        <Ctrl icon={Video} label="Video" />
        <Ctrl icon={MonitorUp} label="Share" />
        <Ctrl icon={MessageSquare} label="Chat" badge="2" />
        <Ctrl icon={UserPlus} label="Add people" />
        <Ctrl icon={PhoneOff} label="Leave" danger />
      </div>
    </div>

    {/* floating callouts */}
    {!bare && (<>
    <div className="absolute left-[46%] top-[13.5rem] hidden w-52 rounded-xl border border-slate-200 bg-white p-3 shadow-xl sm:block">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">In-call chat</p>
      <p className="mt-1.5 rounded-lg rounded-tl-none bg-slate-100 px-2.5 py-1.5 text-xs text-slate-700">Fever since 3 days, 101°F last night</p>
      <p className="mt-1.5 ml-6 rounded-lg rounded-tr-none bg-brand-600 px-2.5 py-1.5 text-xs text-white">Noted. Sending your prescription now.</p>
    </div>
    <div className="absolute -bottom-6 left-6 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-xl">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-care-50 text-care-600"><Users className="h-4 w-4" /></span>
      <div>
        <p className="text-xs font-semibold text-ink-900">Grandmother joined with a secure link</p>
        <p className="text-[10px] text-slate-500">Approved by the doctor</p>
      </div>
    </div>
    </>)}
  </div>
);

/* ------------------------------------------------------------------ the prescription */

const RX_LINES = [
  ["Paracetamol 650 mg tablet", "1 tablet · 3 times a day · after food", "5 days"],
  ["Cetirizine 10 mg tablet", "1 tablet · at bedtime", "5 days"],
  ["ORS sachet", "1 sachet in 1 L water · sip through the day", "3 days"],
];

export const PrescriptionPad = ({ bare = false }) => (
  <div className="relative">
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_30px_70px_-30px_rgba(11,35,73,.45)] sm:p-6">
      <div className="flex items-start justify-between gap-3 border-b border-dashed border-slate-200 pb-4">
        <div>
          <p className="font-display text-base font-bold text-ink-900">Dr. Meera Iyer</p>
          <p className="text-xs text-slate-500">MBBS, MD · General Medicine · Reg. KMC-55821</p>
        </div>
        <span className="font-display text-3xl font-black italic text-brand-600">℞</span>
      </div>
      <div className="grid grid-cols-2 gap-2 py-3 text-xs text-slate-600">
        <p><span className="text-slate-400">Patient</span><br /><b className="text-ink-900">Ananya Sharma</b> · 29 F</p>
        <p className="text-right"><span className="text-slate-400">Video consultation</span><br />30 Oct 2026 · 10:15 AM</p>
      </div>
      <div className="rounded-xl bg-slate-50 p-3 text-xs">
        <p className="text-slate-500">Diagnosis</p>
        <p className="mt-0.5 font-semibold text-ink-900">Viral fever <span className="ml-1 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700">SNOMED 1149222004</span></p>
      </div>
      <ul className="mt-3 space-y-2">
        {RX_LINES.map(([name, how, days]) => (
          <li key={name} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-care-50 text-care-600"><Pill className="h-3.5 w-3.5" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink-900">{name}</p>
              <p className="text-xs text-slate-500">{how}</p>
            </div>
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{days}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <FileSignature className="h-7 w-7 text-brand-600" />
          <span>Prescription no.<br />Rx HF-2026-10421</span>
        </div>
        <div className="text-right">
          <svg viewBox="0 0 120 36" className="ml-auto h-8 w-28 text-brand-700" aria-hidden="true">
            <path d="M4 26 C18 4 26 34 38 18 S56 8 62 24 S82 30 92 12 S110 20 116 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
          <p className="text-[10px] text-slate-400">Digitally signed</p>
        </div>
      </div>
    </div>
    {!bare && <div className="absolute -right-3 -top-5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-xl sm:-right-6">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Send className="h-3.5 w-3.5" /></span>
      <div>
        <p className="text-xs font-semibold text-ink-900">Sent to patient</p>
        <p className="text-[10px] text-slate-500">In the patient app · PDF</p>
      </div>
    </div>}
  </div>
);

/* ------------------------------------------------------------------ section */

const POINTS = [
  [Video, "One-tap video visits", "Patients join from the app or a link – no downloads. Doctors see the queue and start calls from their worklist."],
  [UserPlus, "Bring family into the call", "Doctors add colleagues or relatives; patients can invite family with the doctor's approval."],
  [FileSignature, "Prescribe during the call", "Search medicines and diagnoses, apply your templates, sign digitally – the patient gets it instantly."],
  [Pill, "Medicine reminders that work", "Every prescription becomes a daily dose schedule the patient ticks off, and you can see adherence."],
];

const Telehealth = () => (
  <section id="telehealth" className="relative overflow-hidden py-24 sm:py-28">
    <div className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute left-1/2 top-20 h-[30rem] w-[56rem] -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-100/70 via-violet-100/40 to-care-100/60 blur-3xl" />
    </div>
    <div className="container-x">
      <Reveal className="mx-auto max-w-3xl text-center">
        <span className="eyebrow"><Stethoscope className="h-3.5 w-3.5" /> Video consultations &amp; digital prescriptions</span>
        <h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          See patients anywhere. <span className="text-gradient">Prescribe in seconds.</span>
        </h2>
        <p className="mt-5 text-lg leading-relaxed text-slate-600">
          HD video visits built into your hospital system, with a clinically coded prescription the patient receives before the call even ends.
        </p>
      </Reveal>

      <div className="mt-16 grid items-start gap-14 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-12">
        <Reveal><VideoCallScene /></Reveal>
        <Reveal delay={0.15}><PrescriptionPad /></Reveal>
      </div>

      <div className="mt-20 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {POINTS.map(([Icon, title, body], i) => (
          <Reveal key={title} delay={i * 0.06} className="rounded-2xl border border-slate-200 bg-white p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Icon className="h-5 w-5" /></span>
            <h3 className="mt-4 font-display text-base font-bold text-ink-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <a href="#contact" className="btn-primary px-6 py-3.5 text-base">See a live video consult <ArrowRight className="h-5 w-5" /></a>
        <span className="flex items-center gap-2 text-sm text-slate-500">
          <BadgeCheck className="h-4 w-4 text-care-500" /> ABDM &amp; FHIR-ready prescriptions · <Link2 className="h-4 w-4 text-brand-500" /> Secure guest links
        </span>
      </Reveal>
    </div>
  </section>
);

export default Telehealth;
